import React, { useCallback } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { IconButton, SearchField, NakedButton } from '@frontend/design-system';
import { GlobalSearchTrackingIds } from '../constants';
import { RecentSearches } from '../molecules';
import { useGlobalSearch } from '../provider';
import { staticCss } from './styles';
import type { FocusEvent, StyleConfig } from './types';

export const AllResultsButton = React.memo(() => {
  const { t } = useTranslation('global-search');
  const { searchFor, isExpandedView, debouncedSearchTerm, setIsExpandedView } = useGlobalSearch([
    'searchFor',
    'isExpandedView',
    'debouncedSearchTerm',
    'setIsExpandedView',
  ]);

  const handleClick = useCallback(() => setIsExpandedView(true), [setIsExpandedView]);

  if (isExpandedView) return null;

  return (
    <div css={staticCss.allResultsWrapper}>
      <NakedButton
        trackingId={GlobalSearchTrackingIds.viewAllResults}
        onClick={handleClick}
        css={staticCss.allResultsButton}
      >
        {searchFor === 'all' ? t("All Results for '{{debouncedSearchTerm}}'", { debouncedSearchTerm }) : t('View More')}
      </NakedButton>
    </div>
  );
});
AllResultsButton.displayName = 'AllResultsButton';

export const SearchOverlay = React.memo(() => (
  <div
    css={(themeObj) => css`
      position: fixed;
      inset: 0;
      width: 100%;
      height: 100%;
      z-index: ${themeObj.zIndex.popover - 1};
      top: ${themeObj.heightOffset}px;
    `}
  />
));
SearchOverlay.displayName = 'SearchOverlay';

export const DialogContent = React.memo(
  ({
    searchFieldProps,
    styleConfig,
    isExpandedView,
    isXIconFlex,
    debouncedSearchTerm,
    resultsMeasureRef,
    resultsHeight,
    onDialogSearchSubmit,
    onDialogSearchFocus,
    onDialogSearchBlur,
    onCloseExpandedView,
    children,
  }: {
    searchFieldProps: any;
    styleConfig: StyleConfig;
    isExpandedView: boolean;
    isXIconFlex: boolean;
    isXSmall: boolean;
    debouncedSearchTerm: string;
    resultsMeasureRef: (el: HTMLElement | null) => void;
    resultsHeight: number;
    onDialogSearchSubmit: () => void;
    onDialogSearchFocus: FocusEvent;
    onDialogSearchBlur: FocusEvent;
    onCloseExpandedView: () => void;
    children: React.ReactNode;
  }) => {
    const { t } = useTranslation('global-search');

    const formMaxWidthCss = isExpandedView ? staticCss.formMaxWidthExpanded : staticCss.formMaxWidthDefault;
    const resultsContainerCss = isExpandedView ? staticCss.resultsContainerExpanded : staticCss.resultsContainerDefault;

    return (
      <>
        <motion.div css={[staticCss.dialogInnerFlex, formMaxWidthCss]}>
          <SearchField
            name='Global Search'
            id='global-search-field'
            onSearchSubmit={onDialogSearchSubmit}
            placeholder='Search'
            clearable={false}
            css={styleConfig.dialogSearchCss}
            containerCss={styleConfig.dialogSearchContainerCss}
            {...searchFieldProps}
            onFocus={onDialogSearchFocus}
            onBlur={onDialogSearchBlur}
          />

          {isExpandedView && (
            <IconButton
              trackingId={GlobalSearchTrackingIds.closeExpandedView}
              css={!isXIconFlex ? staticCss.closeButtonAbsolute : undefined}
              label={t('Close Global Search')}
              onClick={onCloseExpandedView}
            >
              <Icon name='x' />
            </IconButton>
          )}
        </motion.div>

        <motion.div css={staticCss.dialogResultsMargin} animate={{ height: resultsHeight }}>
          <motion.div
            ref={resultsMeasureRef}
            css={resultsContainerCss}
            style={{ maxHeight: styleConfig.searchResultsMaxHeight }}
          >
            {!debouncedSearchTerm ? (
              <RecentSearches />
            ) : (
              <>
                {children}
                <AllResultsButton />
              </>
            )}
          </motion.div>
        </motion.div>
      </>
    );
  }
);
DialogContent.displayName = 'DialogContent';
